// Choosing the task another task sits under.
//
// A single-select popover rather than the MultiSelect next door, because a task
// has one parent and a checkbox list that permits two is a control that lies
// about the shape of the data underneath it.
//
// THE LIST COMES FROM THE DATABASE, NOT FROM THE BOARD ROWS IN MEMORY
//
// Filtering the rows the browser already holds would have cost no round trip.
// It would also have written the eligibility rule a second time, in React,
// beside the copy in task_parent_options_v1 that actually enforces it, and this
// project has twice had one number computed in two places and watched the two
// disagree. So the picker asks what it may offer. A choice it shows cannot be
// refused by the write behind it.
//
// Fetched when the popover opens, not with the panel. 500 tasks is a list
// nobody needs until they have decided to move something.
import { useEffect, useMemo, useRef, useState } from 'react'
import { Loader2, Search } from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from '../../lib/shadcn/popover'
import { Input } from '../../lib/shadcn/input'
import { cn } from '../../lib/shadcn/utils'
import { useGetTaskParentOptions } from '../../hooks/backend/tasks'

export type ParentOption = {
  id: string
  ref: number
  title: string
  status: string
  board_group: string
}

export function TaskParentPicker({
  taskId,
  currentParentId,
  onChoose,
  children,
  align = 'start',
}: {
  taskId: string
  currentParentId: string | null
  onChoose: (parentId: string) => Promise<void>
  children: React.ReactNode
  align?: 'start' | 'center' | 'end'
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [saving, setSaving] = useState(false)
  const options = useGetTaskParentOptions()

  const trigger = options.trigger
  useEffect(() => {
    if (!open) return
    setQuery('')
    void trigger({ task_id: taskId }, { skipCache: true })
  }, [open, taskId, trigger])

  const all = (options.data ?? []) as ParentOption[]

  // Number first, because "#412" is what somebody reads off a card or an email
  // and types in. Title search stays, for the times they only remember words.
  const shown = useMemo(() => {
    const q = query.trim().toLowerCase().replace(/^#/, '')
    if (!q) return all.slice(0, 60)
    return all
      .filter((o) => String(o.ref).startsWith(q) || o.title.toLowerCase().includes(q))
      .slice(0, 60)
  }, [all, query])

  // Guards a second click while the first write is in flight. Without it a
  // double click sends two moves and the second one races the reload.
  const busy = useRef(false)

  const choose = async (id: string) => {
    if (busy.current) return
    busy.current = true
    setSaving(true)
    try {
      await onChoose(id)
      setOpen(false)
    } finally {
      busy.current = false
      setSaving(false)
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent align={align} className="w-80 p-0">
        <div className="flex items-center gap-2 border-b border-border px-3 py-2">
          <Search className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <Input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by number or title"
            className="h-7 border-0 p-0 text-sm shadow-none focus-visible:ring-0"
          />
          {(options.loading || saving) && (
            <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-muted-foreground" />
          )}
        </div>
        <div className="max-h-72 overflow-y-auto py-1">
          {/* Three different empty states, because they mean three different
              things and one message for all of them sends somebody looking for
              a fault that is not there. */}
          {options.error && (
            <p className="px-3 py-2 text-sm text-rose-600 dark:text-rose-400">{options.error}</p>
          )}
          {!options.error && options.loading && all.length === 0 && (
            <p className="px-3 py-2 text-sm text-muted-foreground">Loading tasks…</p>
          )}
          {!options.error && !options.loading && all.length === 0 && (
            <p className="px-3 py-2 text-sm text-muted-foreground">
              There is no task this one can go under. Every other open task is either a subtask
              already or has subtasks of its own.
            </p>
          )}
          {!options.error && all.length > 0 && shown.length === 0 && (
            <p className="px-3 py-2 text-sm text-muted-foreground">Nothing matches "{query}".</p>
          )}
          {shown.map((o) => (
            <button
              key={o.id}
              type="button"
              disabled={saving}
              onClick={() => void choose(o.id)}
              className={cn(
                'flex w-full items-start gap-2 px-3 py-1.5 text-left text-sm hover:bg-muted',
                o.id === currentParentId && 'bg-muted',
                saving && 'opacity-60',
              )}
            >
              <span className="shrink-0 tabular-nums text-muted-foreground">#{o.ref}</span>
              <span className="min-w-0 flex-1">{o.title}</span>
              <span className="shrink-0 text-[11px] text-muted-foreground">{o.status}</span>
            </button>
          ))}
          {shown.length === 60 && (
            <p className="px-3 py-1.5 text-[11px] text-muted-foreground">
              First 60 shown. Type to narrow.
            </p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
